import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Box,
  Checkbox,
  Button,
  TablePagination,
  Stack,
  TextField,
  MenuItem,
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { databases } from "../lib/appwrite";

const DB_ID = process.env.REACT_APP_APPWRITE_DB_ID;
const COLLECTION_ID = process.env.REACT_APP_APPWRITE_COLLECTION_ID;

const STATUS_OPTIONS = [
  "Pending",
  "Processing",
  "In Transit",
  "At Customs",
  "Out for Delivery",
  "Delivered",
  "Delayed",
  "Exception",
  "Returned",
  "Cancelled",
];

export default function ShipmentsTable({ shipments, setEditing, onDeleted }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkStatus, setBulkStatus] = useState("");
  const [bulkDetails, setBulkDetails] = useState("");
  const [openRows, setOpenRows] = useState({});

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSelect = (id) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const handleSelectAll = (event, paginatedShipments) => {
    setSelectedIds(
      event.target.checked ? paginatedShipments.map((s) => s.$id) : []
    );
  };

  const formatRecord = (status, details) => {
    const now = new Date();
    const day = now.getDate().toString().padStart(2, "0");
    const month = (now.getMonth() + 1).toString().padStart(2, "0");
    const year = now.getFullYear();
    const hhmm = now.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    const formattedDate = `${day}/${month}/${year} ${hhmm}`;
    return `${formattedDate} - ${status}${details ? ` - ${details}` : ""}`;
  };

  const handleBulkUpdate = async () => {
    if (!selectedIds.length) return;
    try {
      for (const id of selectedIds) {
        const doc = shipments.find((s) => s.$id === id);
        if (!doc) continue;

        const newStatus = bulkStatus || doc.status;
        const newDetails = bulkDetails || doc.details;

        let updatedRecords = Array.isArray(doc.records) ? [...doc.records] : [];
        if (bulkStatus || bulkDetails) {
          updatedRecords.push(formatRecord(newStatus, newDetails));
        }

        await databases.updateDocument(DB_ID, COLLECTION_ID, id, {
          ...doc,
          status: newStatus,
          details: newDetails,
          records: updatedRecords,
        });
      }

      setSelectedIds([]);
      setBulkStatus("");
      setBulkDetails("");
      onDeleted?.();
    } catch (err) {
      alert("Failed to update selected shipments: " + err.message);
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm(`Delete ${selectedIds.length} shipment(s)?`)) return;
    try {
      for (const id of selectedIds) {
        await databases.deleteDocument(DB_ID, COLLECTION_ID, id);
      }
      setSelectedIds([]);
      onDeleted?.();
    } catch (err) {
      alert("Failed to delete selected shipments: " + err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this shipment?")) return;
    try {
      await databases.deleteDocument(DB_ID, COLLECTION_ID, id);
      onDeleted?.();
    } catch (err) {
      alert("Failed to delete: " + err.message);
    }
  };

  if (!shipments || shipments.length === 0)
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="body1" color="text.secondary">
          No active shipments yet.
        </Typography>
      </Box>
    );

  const paginatedShipments = shipments.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Paper>
      {/* Bulk Actions */}
      {selectedIds.length > 0 && (
        <Box sx={{ p: 2, borderBottom: "1px solid #ccc" }}>
          <Typography variant="subtitle1" gutterBottom>
            Bulk Actions ({selectedIds.length} selected)
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems="center"
          >
            <TextField
              select
              label="Status"
              value={bulkStatus}
              onChange={(e) => setBulkStatus(e.target.value)}
              size="small"
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Details"
              value={bulkDetails}
              onChange={(e) => setBulkDetails(e.target.value)}
              size="small"
              sx={{ minWidth: 200 }}
            />
            <Button variant="contained" onClick={handleBulkUpdate}>
              Apply Changes
            </Button>
            <Button variant="outlined" color="error" onClick={handleBulkDelete}>
              Delete Selected
            </Button>
          </Stack>
        </Box>
      )}

      {/* Table */}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={
                    paginatedShipments.length > 0 &&
                    paginatedShipments.every((s) => selectedIds.includes(s.$id))
                  }
                  onChange={(e) => handleSelectAll(e, paginatedShipments)}
                />
              </TableCell>
              <TableCell />
              <TableCell>Tracking #</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Destination</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Details</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedShipments.map((s) => (
              <React.Fragment key={s.$id}>
                <TableRow selected={selectedIds.includes(s.$id)}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedIds.includes(s.$id)}
                      onChange={() => handleSelect(s.$id)}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() =>
                        setOpenRows((prev) => ({
                          ...prev,
                          [s.$id]: !prev[s.$id],
                        }))
                      }
                    >
                      {openRows[s.$id] ? (
                        <KeyboardArrowUpIcon />
                      ) : (
                        <KeyboardArrowDownIcon />
                      )}
                    </IconButton>
                  </TableCell>
                  <TableCell>{s.trackingNumber}</TableCell>
                  <TableCell>{s.customerName}</TableCell>
                  <TableCell>{s.destinationCity}</TableCell>
                  <TableCell>{s.status}</TableCell>
                  <TableCell>{s.details}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      color="primary"
                      onClick={() => setEditing(s)}
                      size="small"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => handleDelete(s.$id)}
                      sx={{ ml: 1 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>

                {/* Collapsible History */}
                <TableRow>
                  <TableCell
                    style={{ paddingBottom: 0, paddingTop: 0 }}
                    colSpan={8}
                  >
                    <Collapse in={openRows[s.$id]} timeout="auto" unmountOnExit>
                      <Box margin={1}>
                        <Typography variant="subtitle2" gutterBottom>
                          History
                        </Typography>
                        <Divider sx={{ mb: 1 }} />
                        <List dense>
                          {Array.isArray(s.records) && s.records.length > 0
                            ? [...s.records]
                                .slice()
                                .reverse()
                                .map((record, idx) => (
                                  <ListItem key={idx} sx={{ py: 0.5 }}>
                                    <ListItemText
                                      primary={
                                        <Typography
                                          variant="body2"
                                          fontWeight={
                                            idx === 0 ? "bold" : "normal"
                                          }
                                        >
                                          {record}
                                        </Typography>
                                      }
                                    />
                                  </ListItem>
                                ))
                            : "No history available."}
                        </List>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={shipments.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
}
